import type { ReactNode } from 'react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import type { ColumnId } from './types'
import type { ColumnDef } from './columnTypes'
import { US_STATES } from '@/utils/geo'
import { stageAgingExceeded, totalAgeDays } from '@/utils/dealAge'
import { counterparty, counterpartyText } from './helpers'
import { ReviewGapMarker, useReviewGap } from './ReviewGapMarker'
import { DerivedTag } from './DerivedTag'
import { LTV_LTC_ELSE, ltvLtcDisplay } from './ltvLtcDisplay'
import { loanCapitalProgram, ltvLtcText } from './dealResolution'
import {
  bookCellEntry,
  debtYieldStabilizedText,
  debtYieldText,
  loanAmortMonths,
  loanAmortReading,
  loanAmortizationType,
  loanBrokerContact,
  loanBrokerage,
  loanCity,
  loanCountry,
  loanCoupon,
  loanCouponBasis,
  loanDebtYield,
  loanDebtYieldStabilized,
  loanExtensionMonths,
  loanIoMonths,
  loanKillReason,
  loanKilledAt,
  loanKilledAtStage,
  loanLtc,
  loanLtvStabilized,
  loanPurpose,
  loanRateType,
  loanReceived,
  loanSpread,
  loanSquareFeet,
  loanState,
  loanTermMonths,
  loanUnitMeasure,
  loanUnits,
  loanUnitsReading,
  subordinateWithoutSenior,
  customValue,
  type MonthsReading,
} from './loanFigures'
import { withDeadBadge } from './deadScope'
import {
  commitmentBasis,
  commitmentSplit,
  requestedBasis,
  requestedCommitment,
  completeness,
  completenessBasis,
  createdIso,
  createdStamp,
  derivedBasis,
  exceptionReading,
  isDerivedField,
  lastModifiedStamp,
  loanExceptions,
  money,
  moneyRecorded,
  pctRecorded,
  presentNumber,
  secondaryUnderwriterOf,
  slaVerdict,
  stageTone,
} from './helpers'

export type { ColumnDef } from './columnTypes'

/** The Sponsor / Borrower cell. A component, so it can read the review gap:
 *  a contested sponsor prints no name beside its rivals marker. */
function SponsorCell({ loan }: { loan: Loan }) {
  const contested = useReviewGap(loan.id, 'sponsor')?.status === 'conflict'
  const sponsor = counterparty(loan)
  const text = counterpartyText(loan, contested)
  return (
    <span className="inline-flex min-w-0 items-center">
      <span
        className={clsx('truncate', sponsor ? 'text-stone-300' : text ? 'text-stone-400' : 'text-stone-600')}
        title={text || undefined}
      >
        {text || '—'}
      </span>
      {/* The borrowing entity copied over because no document names a
          sponsor is a Suggestion, never the deal's stated sponsor (Round 3). */}
      {loan.sponsorSuggested && sponsor ? (
        <span
          className="ml-1 shrink-0 rounded border border-dashed border-stone-500/50 px-1 text-[9px] text-stone-400"
          title="Suggested — copied from the borrowing entity; no document names a sponsor."
        >
          suggested
        </span>
      ) : null}
      <ReviewGapMarker
        loanId={loan.id}
        field="sponsor"
        populated={!!sponsor}
        ingestionId={loan.sourceIngestionId}
      />
    </span>
  )
}

function badge(text: string, tone = 'stone', title = text): ReactNode {
  const tones: Record<string, string> = {
    stone: 'border-stone-500/20 bg-stone-500/[0.08] text-stone-300',
    gold: 'border-amber-300/25 bg-amber-300/[0.08] text-amber-200',
    red: 'border-red-400/25 bg-red-400/[0.08] text-red-300',
    green: 'border-emerald-400/25 bg-emerald-400/[0.08] text-emerald-300',
    blue: 'border-sky-400/20 bg-sky-400/[0.07] text-sky-300',
  }
  // One line, always: the row is a fixed height, so a label that wrapped
  // ("Student Housing") spilled out of its row. A label wider than its column
  // ends in an ellipsis, and the whole of it is on hover — unless the caller
  // has a fuller tooltip (the Exceptions list), which would otherwise be hidden.
  return (
    <span
      className={clsx('inline-flex max-w-full items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[9px] font-medium', tones[tone])}
      title={title}
    >
      <span className="truncate">{text}</span>
    </span>
  )
}

function Completeness({ value, basis }: { value: number | null; basis: string | null }) {
  if (value == null) return <span className="font-mono text-stone-600">—</span>
  return (
    <div className="flex items-center justify-center gap-2" title={basis ?? undefined}>
      <div className="h-1 w-12 overflow-hidden rounded-full bg-stone-800">
        <div
          className={clsx('h-full rounded-full', value >= 80 ? 'bg-emerald-400' : value >= 60 ? 'bg-amber-300' : 'bg-red-400')}
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="font-mono text-stone-400">{value}%</span>
    </div>
  )
}

// `align` is retained on ColumnDef for backwards compatibility with saved
// views, but Report Studio preview and Excel export always center every
// header and value cell.
/** The underwriter on record: the catalogue's `underwriter` field. */
function underwriterOf(loan: Loan): string {
  const value = loan.customFields?.underwriter
  return typeof value === 'string' ? value.trim() : ''
}

function textField(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function analystOf(loan: Loan): string {
  const value = customValue(loan, 'analyst')
  return textField(value)
}

function originatorOf(loan: Loan): string {
  return textField(loan.originator)
}

/** The lending program the deal sits in — capital_program, as the dossier reads
 *  it. One reader (`dealResolution`) for the grid and every module that prints it. */
function capitalProgram(loan: Loan): string | undefined {
  return loanCapitalProgram(loan) ?? undefined
}

/** True when the deal's value for `field` is only the platform's entry default
 *  (`platform_defaults` on the GET /loans row), not a figure anyone stated. */
function isPlatformDefault(loan: Loan, field: string): boolean {
  return (loan.platformDefaults ?? []).includes(field)
}

/** Printed text for a defaultable field: "Floating (default)" when the value is the
 *  entry default — the same mark the server's prints carry (view_grid._default_marked). */
function defaultMarked(loan: Loan, field: string, text: string): string {
  return text && text !== '—' && isPlatformDefault(loan, field) ? `${text} (default)` : text
}

/** A defaultable text cell: the value, with a muted "default" tag when no one stated it. */
function defaultableCell(loan: Loan, field: string, value: string | null, tone = 'text-stone-300'): ReactNode {
  if (!value || !isPlatformDefault(loan, field)) return textCell(value, tone)
  return (
    <span className="truncate" title={`${value} — the platform's entry default; no document or person stated it`}>
      <span className={tone}>{value}</span> <span className="text-stone-500">(default)</span>
    </span>
  )
}

/** A text cell that truncates with the full value on hover; "—" when absent. */
function textCell(value: string | null, tone = 'text-stone-300'): ReactNode {
  return (
    <span className={clsx('truncate', value ? tone : 'text-stone-600')} title={value ?? undefined}>
      {value || '—'}
    </span>
  )
}

/** A figure cell: monospaced, "—" when absent. */
function figureCell(text: string, present: boolean, tone = 'text-stone-300', title?: string): ReactNode {
  return (
    <span className={clsx('font-mono', present ? tone : 'text-stone-600')} title={title}>
      {text}
    </span>
  )
}

/** A count as the grid prints it: grouped ("713,251"), "—" when absent. */
function countText(count: number | null): string {
  return count == null ? '—' : count.toLocaleString('en-US')
}

function monthsCell(reading: MonthsReading, title?: string): ReactNode {
  return figureCell(reading.text ?? '—', reading.text !== null, 'text-stone-300', title)
}

/** A catalogue-derived ratio (LTC, stabilized LTV / debt yield) carries the
 *  "calc" mark, with its formula, when the engine computed it rather than a
 *  document stating it. */
function derivedRatioCell(loan: Loan, ratio: number | null, field: string, text: string): ReactNode {
  const basis = ratio == null ? null : derivedBasis(loan, field)
  return (
    <span
      title={basis ? `${text} · ${basis}` : undefined}
      className={clsx('font-mono', ratio == null ? 'text-stone-600' : basis ? 'text-violet-300' : 'text-stone-300')}
    >
      {text}
      <DerivedTag basis={basis} />
    </span>
  )
}

/** A commitment leg (total, initial advance, future funding) carries the
 *  "calc" mark when the grid computed it or stands the loan amount in for it,
 *  with that basis as the title. A leg the record states is unmarked. */
function commitmentCell(value: number | null, basis: string | null): ReactNode {
  const text = money(value)
  const marked = value == null ? null : basis
  return (
    <span
      title={marked ? `${text} · ${marked}` : undefined}
      className={clsx('font-mono whitespace-nowrap', marked ? 'text-violet-300' : 'text-stone-300')}
    >
      {text}
      <DerivedTag basis={marked} />
    </span>
  )
}

/** The in-place LTV's "calc" basis, or null for a stated or absent LTV. One
 *  condition for the In-place LTV column and the combined LTV / LTC cell. */
function ltvDerivedBasis(loan: Loan): string | null {
  return presentNumber(loan.ltv) != null && isDerivedField(loan, 'ltv')
    ? 'Derived — loan ÷ value; no document states this LTV'
    : null
}

/** The combined In-place LTV / LTC cell's "calc" basis: each side the cell
 *  shows that the platform computed, named, or null when every side shown is
 *  stated. `allDerived` when every side shown is computed. */
function ltvLtcBasis(loan: Loan): { basis: string | null; allDerived: boolean } {
  const ltvShown = presentNumber(loan.ltv) != null
  const ltc = loanLtc(loan)
  // `ltv_else_ltc` shows LTC only for a deal with no in-place LTV.
  const ltcShown = ltc != null && (ltvLtcDisplay() !== LTV_LTC_ELSE || !ltvShown)
  const ltv = ltvShown ? ltvDerivedBasis(loan) : null
  const ltcBasis = ltcShown ? derivedBasis(loan, 'ltc') : null
  const parts = [ltv && `LTV: ${ltv}`, ltcBasis && `LTC: ${ltcBasis}`].filter(Boolean)
  const shown = Number(ltvShown) + Number(ltcShown)
  return {
    basis: parts.length ? parts.join(' · ') : null,
    allDerived: shown > 0 && parts.length === shown,
  }
}

/** The Backshop columns a deal book cell can stand in (view_grid.py `BOOK_CELL_COLUMNS`). */
const BOOK_CELL_COLUMNS: ReadonlySet<ColumnId> = new Set<ColumnId>([
  'type', 'city', 'location', 'unitsMeasured', 'product', 'purpose', 'initialAdvance', 'futureFunding',
  'fullyFunded', 'ltvLtc', 'ltvStabilized', 'debtYield', 'debtYieldStabilized', 'termMonths', 'extensions',
  'ioMonths', 'amortOrIo', 'rateType', 'spread', 'coupon', 'sponsor', 'assignee', 'brokerage', 'brokerContact',
])

/**
 * The Pipeline is a replica of the deal book it was imported from (Edward,
 * 31 Oct 2026): where the record reads a cell otherwise — Units with no
 * measure, State 83 filed as "Multiple locations" — the book's text prints,
 * with what the record holds on hover, until the record's reading changes
 * (view_grid.py `_with_book_cell`). A Backshop 0 is no data and stays blank.
 * Display only: sorting, totals and filters read the record.
 */
function withBookCell(column: ColumnDef): ColumnDef {
  if (!BOOK_CELL_COLUMNS.has(column.id)) return column
  const { display, render } = column
  return {
    ...column,
    display: (loan) => {
      const live = display(loan)
      return bookCellEntry(loan, column.id, live)?.text ?? live
    },
    render: (loan) => {
      const entry = bookCellEntry(loan, column.id, display(loan))
      if (!entry) return render(loan)
      const held = entry.read === '—' ? 'the deal record holds no value' : `the deal record reads ${entry.read}`
      const title = `${entry.text} — as the deal book states it; ${held}`
      if (column.id === 'type') return badge(entry.text, 'stone', title)
      if (column.kind === 'number' || column.align === 'right') return figureCell(entry.text, true, 'text-stone-300', title)
      return (
        <span className="truncate text-stone-300" title={title}>
          {entry.text}
        </span>
      )
    },
  }
}

const RECORD_COLUMN_DEFS: ColumnDef[] = [
  {
    id: 'id',
    label: 'Deal ID',
    group: 'Identity',
    kind: 'text',
    value: (loan) => loan.id,
    display: (loan) => loan.id,
    render: (loan) => <span className="font-mono text-stone-500">{loan.id}</span>,
  },
  {
    id: 'property',
    label: 'Deal / property',
    group: 'Identity',
    kind: 'text',
    value: (loan) => loan.property,
    display: (loan) => loan.property || '—',
    render: (loan) => (
      <div className="min-w-0 text-center">
        <div className="flex min-w-0 items-center justify-center">
          {/* Truncated beside a review pill; the title keeps the full name
              readable (nd-060). */}
          <span
            className="truncate font-medium leading-tight text-stone-100"
            title={loan.property || undefined}
          >
            {loan.property || '—'}
          </span>
          <ReviewGapMarker
            loanId={loan.id}
            field="property_name"
            populated={Boolean(loan.property)}
            ingestionId={loan.sourceIngestionId}
          />
        </div>
        <div className="truncate text-[9px] leading-tight text-stone-600">{loan.id}</div>
      </div>
    ),
  },
  {
    id: 'stage',
    label: 'Stage',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loan.stageIndex,
    display: (loan) => loan.stage,
    render: (loan) =>
      withDeadBadge(
        loan,
        <span className={clsx('inline-flex whitespace-nowrap rounded border px-1.5 py-0.5 text-[9px] font-medium', stageTone(loan.stage))}>
          {loan.stage}
        </span>,
      ),
  },
  {
    id: 'loanAmount',
    align: 'right',
    label: 'Requested',
    group: 'Structure',
    kind: 'number',
    unit: 'money',
    // On one basis for every deal — the total commitment where the split is
    // recorded, else the loan amount (OVR-19). `loan_amount` alone held
    // Calloway's initial advance and The Ardsley's total.
    value: (loan) => requestedCommitment(loan) ?? 0,
    num: (loan) => requestedCommitment(loan),
    display: (loan) => moneyRecorded(requestedCommitment(loan)),
    render: (loan) => {
      const requested = requestedCommitment(loan)
      const text = moneyRecorded(requested)
      // Marked when the request is a total the grid computed (or one the
      // engine derived); the stated loan amount standing as the request is not.
      const basis = requested == null ? null : requestedBasis(loan)
      return (
        <span className="inline-flex min-w-0 items-center">
          <span
            title={basis ? `${text} · ${basis}` : undefined}
            className={clsx(
              'font-mono whitespace-nowrap',
              requested == null ? 'text-stone-600' : basis ? 'text-violet-300' : 'text-stone-200',
            )}
          >
            {text}
            <DerivedTag basis={basis} />
          </span>
          <ReviewGapMarker
            loanId={loan.id}
            field="loan_amount"
            populated={presentNumber(loan.loanAmount) != null}
            ingestionId={loan.sourceIngestionId}
          />
        </span>
      )
    },
  },
  {
    // The catalogue's total commitment (initial advance + future funding). The
    // id is kept so saved views still resolve; it read the money DISBURSED on a
    // closed loan, so every pipeline deal showed "—" and no commitment
    // appeared anywhere on the grid.
    id: 'fullyFunded',
    align: 'right',
    label: 'Total commitment',
    group: 'Structure',
    kind: 'number',
    unit: 'money',
    value: (loan) => commitmentSplit(loan).total ?? -1,
    num: (loan) => commitmentSplit(loan).total,
    display: (loan) => money(commitmentSplit(loan).total),
    render: (loan) => commitmentCell(commitmentSplit(loan).total, commitmentBasis(loan).total),
  },
  {
    id: 'initialAdvance',
    align: 'right',
    label: 'Initial advance',
    group: 'Structure',
    kind: 'number',
    unit: 'money',
    value: (loan) => commitmentSplit(loan).initial ?? -1,
    num: (loan) => commitmentSplit(loan).initial,
    display: (loan) => money(commitmentSplit(loan).initial),
    render: (loan) => commitmentCell(commitmentSplit(loan).initial, commitmentBasis(loan).initial),
  },
  {
    id: 'futureFunding',
    align: 'right',
    label: 'Future funding',
    group: 'Structure',
    kind: 'number',
    unit: 'money',
    value: (loan) => commitmentSplit(loan).future ?? -1,
    num: (loan) => commitmentSplit(loan).future,
    display: (loan) => money(commitmentSplit(loan).future),
    render: (loan) => commitmentCell(commitmentSplit(loan).future, commitmentBasis(loan).future),
  },
  {
    // Kept as `location` so saved views that already store this column id keep
    // working; only what it MEANS changed. The underlying text arrives as a
    // metro, a city/state pair or a full street address, which made the column
    // unsortable and ungroupable — three ways of saying where a deal is. It now
    // resolves to the state, with the original text on hover so nothing is lost.
    // Labelled State (Backshop's header): the catalogue's own State, when the
    // record states one, leads over the state the location text resolves to.
    id: 'location',
    label: 'State',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loanState(loan) ?? '—',
    display: (loan) => loanState(loan) ?? '—',
    render: (loan) => {
      const state = loanState(loan)
      const name = state ? US_STATES[state] : undefined
      return (
        <span
          className={clsx('font-mono', state ? 'text-stone-300' : 'text-stone-600')}
          title={[name, loan.location].filter(Boolean).join(' — ') || 'No location recorded'}
        >
          {state ?? '—'}
        </span>
      )
    },
  },
  {
    // City — the catalogue's, else the one the location text names.
    id: 'city',
    label: 'City',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loanCity(loan) ?? '',
    display: (loan) => loanCity(loan) ?? '—',
    render: (loan) => textCell(loanCity(loan)),
  },
  {
    // The catalogue's Country: United States for a state, the named country
    // for an international deal. Not a Backshop column.
    id: 'country',
    label: 'Country',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loanCountry(loan) ?? '',
    display: (loan) => loanCountry(loan) ?? '—',
    render: (loan) => textCell(loanCountry(loan)),
  },
  {
    // Backshop's "Property Type". The id stays `type` so saved views resolve.
    id: 'type',
    label: 'Property type',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loan.type,
    display: (loan) => loan.type || '—',
    render: (loan) => badge(loan.type || 'Missing', loan.type ? 'stone' : 'red'),
  },
  {
    // The count of units, rooms or beds — never a square-foot area. Read from
    // the catalogue (`units`), as the dossier's Units / Keys row does; the
    // measure is on hover.
    id: 'units',
    align: 'right',
    label: 'Units',
    group: 'Identity',
    kind: 'number',
    unit: 'int',
    value: (loan) => loanUnits(loan) ?? -1,
    num: (loan) => loanUnits(loan),
    // Grouped in Excel as it is on screen (1,200, not 1200).
    excel: (loan) => {
      const units = loanUnits(loan)
      return units == null ? null : { value: units, numFmt: '#,##0' }
    },
    display: (loan) => countText(loanUnits(loan)),
    render: (loan) => {
      const units = loanUnits(loan)
      return figureCell(
        countText(units),
        units != null,
        'text-stone-300',
        units == null ? undefined : `${countText(units)} ${loanUnitMeasure(loan).toLowerCase()}`,
      )
    },
  },
  {
    // The area in square feet. Backshop's Units column holds an office's SF
    // under "Units"; the Units column here counts units, rooms and beds only
    // (as the dossier's Units / Keys row does), so the area is its own column.
    id: 'sf',
    align: 'right',
    label: 'Square feet',
    group: 'Identity',
    kind: 'number',
    unit: 'int',
    value: (loan) => loanSquareFeet(loan) ?? -1,
    num: (loan) => loanSquareFeet(loan),
    excel: (loan) => {
      const area = loanSquareFeet(loan)
      return area == null ? null : { value: area, numFmt: '#,##0' }
    },
    display: (loan) => countText(loanSquareFeet(loan)),
    render: (loan) => figureCell(countText(loanSquareFeet(loan)), loanSquareFeet(loan) != null),
  },
  {
    // Backshop's "Units" as the Pipeline report prints it: the primary count WITH
    // the measure it is counted in ("312 Units", "180 Rooms", "713,251 SF"). Text,
    // not a number: a book mixes units, rooms and square feet, and a sum across
    // them is not a figure.
    id: 'unitsMeasured',
    label: 'Units (with measure)',
    group: 'Identity',
    kind: 'text',
    value: (loan) => loanUnitsReading(loan) ?? '',
    display: (loan) => loanUnitsReading(loan) ?? '—',
    render: (loan) => figureCell(loanUnitsReading(loan) ?? '—', loanUnitsReading(loan) != null),
  },
  {
    id: 'product',
    label: 'Capital program',
    group: 'Structure',
    kind: 'enum',
    // GET /loans serves capital_program (no product key): reading product
    // alone left the column blank on every deal (the dossier reads the same).
    value: (loan) => capitalProgram(loan) ?? '',
    display: (loan) => defaultMarked(loan, 'capital_program', capitalProgram(loan) || '—'),
    render: (loan) => (
      <span className="whitespace-nowrap">{defaultableCell(loan, 'capital_program', capitalProgram(loan) ?? null, 'text-stone-400')}</span>
    ),
  },
  {
    // The deal's loan purpose (Acquisition, Refinance, Bridge…), as stated.
    id: 'purpose',
    label: 'Loan purpose',
    group: 'Structure',
    kind: 'enum',
    value: (loan) => loanPurpose(loan) ?? '',
    display: (loan) => loanPurpose(loan) ?? '—',
    render: (loan) => textCell(loanPurpose(loan)),
  },
  // One counterparty column, not two. Sponsor and borrower were one party on
  // this desk's deals, and the grid carried both — two headers, two
  // near-identical strings, one answer. The cell is the sponsor; with no
  // sponsor recorded it names the borrowing entity AS one, and while the
  // sponsor is contested it names nobody (other-11).
  {
    id: 'sponsor',
    label: 'Sponsor / Borrower',
    group: 'Identity',
    kind: 'text',
    value: (loan) => counterpartyText(loan),
    // The grid draws the "suggested" chip below; an export has only text, so it
    // carries the same words the priority column does. Held cell for cell to the
    // server's view_grid.py `_sponsor` by view_grid_parity.json.
    display: (loan) => {
      const name = counterpartyText(loan)
      if (!name) return '—'
      return loan.sponsorSuggested && counterparty(loan) ? `${name} (suggested)` : name
    },
    render: (loan) => <SponsorCell loan={loan} />,
  },
  {
    id: 'broker',
    label: 'Broker',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loan.brokerName ?? '',
    display: (loan) => loan.brokerName || '—',
    render: (loan) => <span className="whitespace-nowrap text-stone-400">{loan.brokerName || '—'}</span>,
  },
  {
    // The brokerage FIRM (Cushman & Wakefield), as distinct from the individual
    // broker above. The catalogue's `brokerage_company`.
    id: 'brokerage',
    label: 'Brokerage',
    group: 'Identity',
    kind: 'enum',
    value: (loan) => loanBrokerage(loan) ?? '',
    display: (loan) => loanBrokerage(loan) ?? '—',
    render: (loan) => textCell(loanBrokerage(loan), 'text-stone-400'),
  },
  {
    // Backshop's "Broker Contact": the catalogue's `broker_contact`, else the
    // broker on the deal — the Pipeline Report's own rule.
    id: 'brokerContact',
    label: 'Broker contact',
    group: 'Identity',
    kind: 'text',
    value: (loan) => loanBrokerContact(loan) ?? '',
    display: (loan) => loanBrokerContact(loan) ?? '—',
    render: (loan) => textCell(loanBrokerContact(loan), 'text-stone-400'),
  },
  {
    // The deal's owner — the catalogue's 'Responsible Party' (assignee). It
    // was labelled 'Underwriter', so Calloway read 'Marcus Chen' under
    // Underwriter while its underwriter on record is Priya Natarajan
    // (OVR-18, UW-03, LPR-19). The id stays 'assignee' so saved views resolve.
    id: 'assignee',
    label: 'Responsible Party',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loan.assignee,
    display: (loan) => loan.assignee || '—',
    render: (loan) => <span className="whitespace-nowrap text-stone-300">{loan.assignee || '—'}</span>,
  },
  {
    // The underwriter on record — the catalogue field, not the assignee.
    id: 'underwriter',
    label: 'Underwriter',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => underwriterOf(loan),
    display: (loan) => underwriterOf(loan) || '—',
    render: (loan) => (
      <span className="whitespace-nowrap text-stone-300">{underwriterOf(loan) || '—'}</span>
    ),
  },
  {
    // Gate 0 F1 (Chris Young, 15 Oct 2026): the closers' underwriter - the
    // catalogue's `secondary_underwriter`. Its own field: the Responsible Party
    // stays with the initial underwriter, and a blank is a dash, never a name
    // borrowed from either. Not one of Backshop's 26 report columns.
    id: 'secondaryUnderwriter',
    label: 'Secondary Underwriter',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => secondaryUnderwriterOf(loan),
    display: (loan) => secondaryUnderwriterOf(loan) || '—',
    render: (loan) => textCell(secondaryUnderwriterOf(loan) || null, 'text-stone-300'),
  },
  {
    // Backshop's "In Place LTV". The id stays `ltv`; the stabilized figure is
    // its own column and never falls back to this one.
    id: 'ltv',
    align: 'right',
    label: 'In-place LTV',
    group: 'Credit',
    kind: 'number',
    unit: 'pct',
    value: (loan) => loan.ltv,
    num: (loan) => presentNumber(loan.ltv),
    display: (loan) => pctRecorded(loan.ltv),
    render: (loan) => {
      // A derived LTV is computed, not stated: it is marked, and it is never
      // painted as a policy breach (see `loanExceptions`).
      const basis = ltvDerivedBasis(loan)
      const derived = basis != null
      return (
        <span
          title={basis ?? undefined}
          className={clsx(
            'font-mono',
            presentNumber(loan.ltv) == null
              ? 'text-stone-600'
              : derived
                ? 'text-violet-300'
                : loan.ltv > 0.7
                  ? 'text-red-300'
                  : 'text-stone-300',
          )}
        >
          {pctRecorded(loan.ltv)}
          {derived && <span className="ml-0.5 text-[8px] uppercase text-violet-300/80">calc</span>}
          <ReviewGapMarker
            loanId={loan.id}
            field="ltv"
            populated={presentNumber(loan.ltv) != null}
            ingestionId={loan.sourceIngestionId}
          />
        </span>
      )
    },
  },
  {
    // Loan-to-cost, from the catalogue (a percent held in points, so ÷100).
    id: 'ltc',
    align: 'right',
    label: 'LTC',
    group: 'Credit',
    kind: 'number',
    unit: 'pct',
    value: (loan) => loanLtc(loan) ?? -1,
    num: (loan) => loanLtc(loan),
    display: (loan) => pctRecorded(loanLtc(loan)),
    render: (loan) => derivedRatioCell(loan, loanLtc(loan), 'ltc', pctRecorded(loanLtc(loan))),
  },
  {
    // Backshop's "In Place LTV/LTC" as one cell: "67.7% / 72.1%". A side the
    // record does not state reads "—" ("67.7% / —"); with neither, the cell is
    // "—" — never a 0.0%. The two columns above stay for sorting and totals.
    id: 'ltvLtc',
    label: 'In-place LTV / LTC',
    group: 'Credit',
    kind: 'text',
    value: (loan) => ltvLtcText(loan) ?? '',
    display: (loan) => ltvLtcText(loan) ?? '—',
    // A derived side carries the "calc" mark the LTV and LTC columns show for
    // it, its basis naming which side; the cell is painted derived only when
    // every side it shows is.
    render: (loan) => {
      const text = ltvLtcText(loan)
      const { basis, allDerived } = text == null ? { basis: null, allDerived: false } : ltvLtcBasis(loan)
      return (
        <span
          title={basis ? `${text} · ${basis}` : undefined}
          className={clsx(
            'font-mono whitespace-nowrap',
            text == null ? 'text-stone-600' : allDerived ? 'text-violet-300' : 'text-stone-300',
          )}
        >
          {text ?? '—'}
          <DerivedTag basis={basis} />
        </span>
      )
    },
  },
  {
    // The stabilized LTV: the catalogue's `ltv_stabilized`, never the in-place one.
    id: 'ltvStabilized',
    align: 'right',
    label: 'Stabilized LTV',
    group: 'Credit',
    kind: 'number',
    unit: 'pct',
    value: (loan) => loanLtvStabilized(loan) ?? -1,
    num: (loan) => loanLtvStabilized(loan),
    display: (loan) => pctRecorded(loanLtvStabilized(loan)),
    render: (loan) =>
      derivedRatioCell(loan, loanLtvStabilized(loan), 'ltv_stabilized', pctRecorded(loanLtvStabilized(loan))),
  },
  {
    id: 'dscr',
    align: 'right',
    label: 'DSCR',
    group: 'Credit',
    kind: 'number',
    unit: 'x',
    value: (loan) => loan.dscr,
    num: (loan) => presentNumber(loan.dscr),
    display: (loan) => (presentNumber(loan.dscr) == null ? '—' : `${loan.dscr.toFixed(2)}x`),
    render: (loan) => {
      // A derived DSCR is marked like a derived LTV, with its formula — The
      // Ardsley's reads NOI / annual debt service (nd3-10) — and, like it, is
      // never painted as a policy breach.
      const basis = presentNumber(loan.dscr) == null ? null : derivedBasis(loan, 'dscr')
      return (
      <span
        title={basis ?? undefined}
        className={clsx(
          'font-mono',
          presentNumber(loan.dscr) == null
            ? 'text-stone-600'
            : basis
              ? 'text-violet-300'
              : loan.dscr < 1.25
                ? 'text-red-300'
                : 'text-stone-300',
        )}
      >
        {presentNumber(loan.dscr) == null ? '—' : `${loan.dscr.toFixed(2)}x`}
        <DerivedTag basis={basis} />
        <ReviewGapMarker
          loanId={loan.id}
          field="dscr"
          populated={presentNumber(loan.dscr) != null}
          ingestionId={loan.sourceIngestionId}
        />
      </span>
      )
    },
  },
  {
    // Backshop's "In Place Debt Yield". The id stays `debtYield`.
    id: 'debtYield',
    align: 'right',
    label: 'In-place debt yield',
    group: 'Credit',
    kind: 'number',
    unit: 'pct',
    // The catalogue debt yield, as a ratio: GET /loans has no top-level key.
    value: (loan) => loanDebtYield(loan) ?? 0,
    num: (loan) => loanDebtYield(loan),
    display: (loan) => debtYieldText(loan, (n) => pctRecorded(n)),
    render: (loan) => {
      const basis = loanDebtYield(loan) == null || subordinateWithoutSenior(loan)
        ? null
        : derivedBasis(loan, 'debt_yield')
      return (
      <span
        className={clsx(
          'font-mono',
          loanDebtYield(loan) == null
            ? 'text-stone-600'
            : basis
              ? 'text-violet-300'
              : (loanDebtYield(loan) as number) < 0.08
                ? 'text-red-300'
                : 'text-stone-300',
        )}
        title={basis ? `${debtYieldText(loan, (n) => pctRecorded(n))} · ${basis}` : debtYieldText(loan, (n) => pctRecorded(n))}
      >
        {/* A subordinate note with no stated senior debt reads n/a; the
            full reason is the title and the export text. */}
        {subordinateWithoutSenior(loan) ? 'n/a' : debtYieldText(loan, (n) => pctRecorded(n))}
        <DerivedTag basis={basis} />
      </span>
      )
    },
  },
  {
    // The stabilized debt yield: the catalogue's `debt_yield_stabilized`, never
    // the in-place one. Withheld, like the in-place yield, for a subordinate
    // note whose senior debt is not stated.
    id: 'debtYieldStabilized',
    align: 'right',
    label: 'Stabilized debt yield',
    group: 'Credit',
    kind: 'number',
    unit: 'pct',
    value: (loan) => loanDebtYieldStabilized(loan) ?? -1,
    num: (loan) => loanDebtYieldStabilized(loan),
    display: (loan) => debtYieldStabilizedText(loan, (n) => pctRecorded(n)),
    render: (loan) => {
      const withheld = subordinateWithoutSenior(loan)
      const ratio = loanDebtYieldStabilized(loan)
      const text = debtYieldStabilizedText(loan, (n) => pctRecorded(n))
      const basis = ratio == null || withheld ? null : derivedBasis(loan, 'debt_yield_stabilized')
      return (
        <span
          className={clsx('font-mono', ratio == null ? 'text-stone-600' : basis ? 'text-violet-300' : 'text-stone-300')}
          title={basis ? `${text} · ${basis}` : text}
        >
          {withheld ? 'n/a' : text}
          <DerivedTag basis={basis} />
        </span>
      )
    },
  },
  {
    // The initial loan term in months, as the dossier's Loan Term row reads it.
    id: 'termMonths',
    align: 'right',
    label: 'Initial term',
    group: 'Structure',
    kind: 'number',
    unit: 'months',
    value: (loan) => loanTermMonths(loan).num ?? -1,
    num: (loan) => loanTermMonths(loan).num,
    display: (loan) => loanTermMonths(loan).text ?? '—',
    render: (loan) => monthsCell(loanTermMonths(loan)),
  },
  {
    // Months of ONE extension option (the catalogue's `extensions`); how many
    // options the deal has is not stored.
    id: 'extensions',
    align: 'right',
    label: 'Extensions',
    group: 'Structure',
    kind: 'number',
    unit: 'months',
    value: (loan) => loanExtensionMonths(loan).num ?? -1,
    num: (loan) => loanExtensionMonths(loan).num,
    display: (loan) => loanExtensionMonths(loan).text ?? '—',
    render: (loan) => monthsCell(loanExtensionMonths(loan), 'Months per extension option'),
  },
  {
    id: 'ioMonths',
    align: 'right',
    label: 'IO period',
    group: 'Structure',
    kind: 'number',
    unit: 'months',
    value: (loan) => loanIoMonths(loan).num ?? -1,
    num: (loan) => loanIoMonths(loan).num,
    display: (loan) => loanIoMonths(loan).text ?? '—',
    render: (loan) => monthsCell(loanIoMonths(loan)),
  },
  {
    // Amortization months. An interest-only loan has none: its Amortization
    // type column says so, and this one stays empty rather than print a zero.
    id: 'amortTerm',
    align: 'right',
    label: 'Amort term',
    group: 'Structure',
    kind: 'number',
    unit: 'months',
    value: (loan) => loanAmortMonths(loan).num ?? -1,
    num: (loan) => loanAmortMonths(loan).num,
    display: (loan) => loanAmortMonths(loan).text ?? '—',
    render: (loan) => monthsCell(loanAmortMonths(loan)),
  },
  {
    // Backshop's "Amort Term": months, or "IO" for an interest-only loan. Text —
    // the months column above stays the one to sort and total.
    id: 'amortOrIo',
    label: 'Amort term / IO',
    group: 'Structure',
    kind: 'text',
    value: (loan) => loanAmortReading(loan) ?? '',
    display: (loan) => loanAmortReading(loan) ?? '—',
    render: (loan) => textCell(loanAmortReading(loan)),
  },
  {
    id: 'amortizationType',
    label: 'Amortization type',
    group: 'Structure',
    kind: 'enum',
    value: (loan) => loanAmortizationType(loan) ?? '',
    display: (loan) => loanAmortizationType(loan) ?? '—',
    render: (loan) => textCell(loanAmortizationType(loan)),
  },
  {
    id: 'rateType',
    label: 'Rate type',
    group: 'Structure',
    kind: 'enum',
    value: (loan) => loanRateType(loan) ?? '',
    display: (loan) => defaultMarked(loan, 'rate_type', loanRateType(loan) ?? '—'),
    render: (loan) => defaultableCell(loan, 'rate_type', loanRateType(loan)),
  },
  {
    // Show the stated spread in percent; the stored value and its scale stay
    // unchanged (`loanSpread`).
    id: 'spread',
    align: 'right',
    label: 'Spread',
    group: 'Structure',
    kind: 'text',
    value: (loan) => loanSpread(loan) ?? '',
    display: (loan) => loanSpread(loan) ?? '—',
    render: (loan) => textCell(loanSpread(loan)),
  },
  {
    // Keep the coupon's index and show its spread in percent (SOFR + 3.5%).
    // `min_coupon` (needs Louis's written yes) prints floor + spread and
    // marks the cell calculated; the stored rate is never rewritten.
    id: 'coupon',
    align: 'right',
    label: 'Coupon',
    group: 'Structure',
    kind: 'text',
    value: (loan) => loanCoupon(loan) ?? '',
    display: (loan) => loanCoupon(loan) ?? '—',
    render: (loan) => {
      const text = loanCoupon(loan)
      const basis = loanCouponBasis(loan)
      return (
        <span
          className={clsx('truncate', text ? 'text-stone-300' : 'text-stone-600')}
          title={basis ? `${text} · ${basis}` : text ?? undefined}
        >
          {text || '—'}
          <DerivedTag basis={basis} />
        </span>
      )
    },
  },
  {
    // Register F1/F2 — the assignment clock. `underwriting/sla.py` has computed
    // this for every loan read for a while and nothing displayed it, so a deal
    // sitting overdue with its owner was invisible unless somebody read the
    // JSON. Deliberately beside "In Stage" below rather than replacing it: a
    // deal can be young in its stage and long overdue with a person.
    //
    // This one IS coloured where "In Stage" is not, and the difference is the
    // threshold's provenance. That column declines to colour because a global
    // cutoff would be invented here; this one carries the per-stage,
    // administrator-set threshold the server sends with every row, so the tone
    // reflects a limit the desk actually agreed to.
    id: 'sla',
    align: 'right',
    label: 'Owner SLA',
    group: 'Workflow',
    kind: 'number',
    unit: 'days',
    // Sorted by how far PAST threshold the deal is, so the worst breach leads
    // rather than merely the longest-held deal.
    value: (loan) =>
      loan.sla?.running && loan.sla.elapsedBusinessDays !== null
        ? loan.sla.elapsedBusinessDays - loan.sla.thresholdDays
        : '',
    num: (loan) =>
      loan.sla?.running && loan.sla.elapsedBusinessDays !== null
        ? loan.sla.elapsedBusinessDays - loan.sla.thresholdDays
        : null,
    // Excel holds the elapsed days (a number it can sort and sum) formatted
    // with this deal's threshold, so the cell reads "17/2d" like the grid.
    excel: (loan) =>
      loan.sla?.running && loan.sla.elapsedBusinessDays !== null
        ? { value: loan.sla.elapsedBusinessDays, numFmt: `0"/${loan.sla.thresholdDays}d"` }
        : null,
    display: (loan) => {
      const sla = loan.sla
      if (!sla?.running || sla.elapsedBusinessDays === null) return '—'
      return `${sla.elapsedBusinessDays}/${sla.thresholdDays}d`
    },
    render: (loan) => {
      const sla = loan.sla
      // Nothing assigned means no clock running, which is a real state and not
      // a zero — an unowned deal is not "on time".
      if (!sla?.running || sla.elapsedBusinessDays === null) {
        return <span className="font-mono text-stone-600">—</span>
      }
      const verdict = slaVerdict(loan)
      return (
        <span
          className={clsx(
            'font-mono',
            verdict === 'escalated' && 'text-red-300',
            verdict === 'breached' && 'text-amber-300',
            verdict === 'running' && 'text-stone-400',
          )}
          title={
            sla.owner
              ? `${sla.owner} — ${sla.elapsedBusinessDays} of ${sla.thresholdDays} business days`
              : `${sla.elapsedBusinessDays} of ${sla.thresholdDays} business days`
          }
        >
          {sla.elapsedBusinessDays}/{sla.thresholdDays}d
        </span>
      )
    },
  },
  {
    // Register E5 — the aging metric the desk manages by. Measured against the
    // CURRENT stage only: seven days in Triage then three at Quote reads three,
    // never ten. Served by the API as days_in_status, derived from the
    // append-only stage lineage, so it cannot drift from the B3 timeline.
    //
    // Deliberately uncoloured. Thresholds are per-stage and administrator-set
    // (register F1); until that lands, inventing a single global cutoff here
    // would put a number on screen the desk never agreed to.
    id: 'daysInStatus',
    align: 'right',
    label: 'In Stage',
    group: 'Workflow',
    kind: 'number',
    unit: 'days',
    // An unknown dwell sorts as empty rather than 0 — a deal with no usable
    // start timestamp must not rank alongside one that genuinely arrived today.
    value: (loan) => loan.daysInStatus ?? '',
    num: (loan) => loan.daysInStatus ?? null,
    display: (loan) =>
      loan.daysInStatus === null || loan.daysInStatus === undefined
        ? '—'
        : `${loan.daysInStatus}d`,
    render: (loan) => (
      <span className="font-mono text-stone-400">
        {loan.daysInStatus === null || loan.daysInStatus === undefined
          ? '—'
          : `${loan.daysInStatus}d`}
      </span>
    ),
  },
  {
    // Cumulative age since the deal entered the system. Retained under a label
    // that says what it is — E5 replaces it as *the* aging metric but "how long
    // have we had this" is still a real question the daily log answers.
    // The id stays 'daysOpen' so saved views and scheduled reports referencing
    // it keep resolving.
    id: 'daysOpen',
    align: 'right',
    label: 'Total Age',
    group: 'Workflow',
    kind: 'number',
    unit: 'days',
    // Never the stored `daysOpen` (0 on every deal): an age that cannot be told is a dash, not "0d".
    value: (loan) => totalAgeDays(loan) ?? '',
    num: (loan) => totalAgeDays(loan),
    display: (loan) => {
      const age = totalAgeDays(loan)
      return age == null ? '—' : `${age}d`
    },
    render: (loan) => {
      const age = totalAgeDays(loan)
      // Amber marks the deal "Stage aging" flags — the same rule as the Exceptions column's, on the
      // time in the stage, not on this cumulative age.
      return (
        <span className={clsx('font-mono', stageAgingExceeded(loan) ? 'text-amber-300' : 'text-stone-400')}>
          {age == null ? '—' : `${age}d`}
        </span>
      )
    },
  },
  {
    // The id stays 'originated' so every saved view, scheduled report and
    // default column set that already references it keeps resolving. Only the
    // SOURCE changed: this column is labelled Created and now reads the
    // record's creation timestamp, which intake stamps when it parses an
    // inquiry and promotes it. It used to read loans.originated — a business
    // date written only when a deal FUNDS — so it was empty for every deal in
    // the pipeline, which is the whole point of the column.
    // Labelled Date added since 29 Oct 2026 (R-12): the desk asks which deal was
    // just added, and "Created" did not read as the answer beside Date received.
    id: 'originated',
    label: 'Date added',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => createdIso(loan),
    display: (loan) => createdStamp(loan),
    render: (loan) => (
      <span className="whitespace-nowrap font-mono text-stone-500">{createdStamp(loan)}</span>
    ),
  },
  {
    // The deal's last change (R-12): the record's updated_at, which every save
    // to the deal moves, a person's or the platform's. The value is the desk
    // stamp itself, so a sort and a date filter read the day the cell prints,
    // as the server's report design does for a column it has no rule for.
    id: 'lastModified',
    label: 'Last modified',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => {
      const stamp = lastModifiedStamp(loan)
      return stamp === '—' ? '' : stamp
    },
    display: (loan) => lastModifiedStamp(loan),
    render: (loan) => (
      <span className="whitespace-nowrap font-mono text-stone-500">{lastModifiedStamp(loan)}</span>
    ),
  },
  {
    // The deal's Date Received — the catalogue's own field (`originated`), the
    // source email's day in New York. Not Created above, which is when the
    // record entered the pipeline. A calendar day: never shifted by a zone.
    id: 'received',
    label: 'Date received',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loanReceived(loan) ?? '',
    display: (loan) => loanReceived(loan) ?? '—',
    render: (loan) => figureCell(loanReceived(loan) ?? '—', loanReceived(loan) != null, 'text-stone-500'),
  },
  {
    // When a dead deal was killed, as the desk's calendar day. A live deal has
    // no kill record: reopening clears it.
    id: 'killedAt',
    label: 'Date killed',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loanKilledAt(loan) ?? '',
    display: (loan) => loanKilledAt(loan) ?? '—',
    render: (loan) => figureCell(loanKilledAt(loan) ?? '—', loanKilledAt(loan) != null, 'text-stone-500'),
  },
  {
    id: 'killReason',
    label: 'Kill reason',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loanKillReason(loan) ?? '',
    display: (loan) => loanKillReason(loan) ?? '—',
    render: (loan) => textCell(loanKillReason(loan), 'text-stone-400'),
  },
  {
    id: 'killedAtStage',
    label: 'Killed at stage',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loanKilledAtStage(loan) ?? '',
    display: (loan) => loanKilledAtStage(loan) ?? '—',
    render: (loan) => textCell(loanKilledAtStage(loan), 'text-stone-400'),
  },
  {
    id: 'priority',
    label: 'Priority',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loan.priority,
    display: (loan) => (loan.prioritySuggested ? `${loan.priority} (suggested)` : loan.priority),
    // A platform default is a suggestion, not the desk's call (MOD-32).
    render: (loan) =>
      loan.prioritySuggested ? (
        <span
          className="rounded border border-dashed border-stone-500/50 px-1 text-[10px] text-stone-400"
          title="Suggested — the platform's default priority. No rule or person has set it."
        >
          {loan.priority} · suggested
        </span>
      ) : (
        badge(loan.priority, loan.priority === 'High' ? 'red' : loan.priority === 'Medium' ? 'gold' : 'green')
      ),
  },
  {
    // Register B2 — "Data Ready" meant nothing to the desk in session, and the
    // client confirmed it gets a plain-language label. The number is the
    // platform's one completeness measure — catalogue fields holding a value,
    // out of the deal catalogue (see `completeness` in helpers.ts) — the same
    // figure the Deal Locator and the dossier export report, with its basis.
    id: 'completeness',
    align: 'right',
    label: 'Fields complete',
    group: 'Governance',
    kind: 'number',
    unit: 'points',
    value: (loan) => completeness(loan) ?? -1,
    num: (loan) => completeness(loan),
    display: (loan) => {
      const value = completeness(loan)
      const basis = completenessBasis(loan)
      return value == null ? '—' : basis ? `${value}% (${basis})` : `${value}%`
    },
    render: (loan) => <Completeness value={completeness(loan)} basis={completenessBasis(loan)} />,
  },
  {
    id: 'exceptions',
    align: 'center',
    label: 'Exceptions',
    group: 'Governance',
    kind: 'number',
    unit: 'int',
    // An unknown reading (no credit box screen and nothing else listed) sorts as empty and prints the dash,
    // never 0 or "Clear" (see `exceptionReading`).
    value: (loan) => exceptionReading(loan) ?? '',
    num: (loan) => exceptionReading(loan),
    display: (loan) => {
      const count = exceptionReading(loan)
      return count == null ? '—' : String(count)
    },
    render: (loan) => {
      const count = exceptionReading(loan)
      if (count == null) {
        return (
          <span className="font-mono text-stone-500" title="No credit box screen is recorded for this deal">
            —
          </span>
        )
      }
      const issues = loanExceptions(loan)
      // The tooltip lists each exception with its basis and, while Credit Box
      // v2.0 awaits the client's sign-off, says the thresholds are not yet
      // ratified policy (exec-03).
      const note = (loan.creditBoxExceptions ?? []).length ? loan.creditBoxRulesNote : null
      const title = [...issues, ...(note ? [note] : [])].join('\n')
      const label = count ? `${count} open` : 'Clear'
      return badge(label, count ? 'red' : 'green', title || label)
    },
  },
  {
    id: 'fundedAmount',
    align: 'right',
    label: 'Funded amount',
    group: 'Structure',
    kind: 'number',
    unit: 'money',
    value: (loan) => presentNumber(loan.fundedAmount) ?? -1,
    num: (loan) => presentNumber(loan.fundedAmount),
    display: (loan) => money(presentNumber(loan.fundedAmount)),
    render: (loan) => figureCell(money(presentNumber(loan.fundedAmount)), presentNumber(loan.fundedAmount) != null),
  },
  {
    id: 'dealStatus',
    label: 'Deal status',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loan.dealStatus ?? '',
    display: (loan) => textField(loan.dealStatus) || '—',
    render: (loan) => textCell(textField(loan.dealStatus) || null),
  },
  {
    id: 'currentStatusDate',
    label: 'Current loan status date',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loan.currentStatusDate ?? '',
    display: (loan) => textField(loan.currentStatusDate) || '—',
    render: (loan) => textCell(textField(loan.currentStatusDate) || null),
  },
  {
    id: 'currentStatusReason',
    label: 'Current status reason',
    group: 'Workflow',
    kind: 'text',
    value: () => '',
    display: () => '—',
    render: () => textCell(null),
  },
  {
    id: 'currentStatusComments',
    label: 'Current status comments',
    group: 'Workflow',
    kind: 'text',
    value: () => '',
    display: () => '—',
    render: () => textCell(null),
  },
  {
    id: 'previousLoanStatus',
    label: 'Previous loan status',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => loan.previousLoanStatus ?? '',
    display: (loan) => textField(loan.previousLoanStatus) || '—',
    render: (loan) => textCell(textField(loan.previousLoanStatus) || null),
  },
  {
    id: 'previousNote',
    label: 'Previous note',
    group: 'Workflow',
    kind: 'text',
    value: () => '',
    display: () => '—',
    render: () => textCell(null),
  },
  {
    id: 'previousManager',
    label: 'Previous manager',
    group: 'Workflow',
    kind: 'text',
    value: () => '',
    display: () => '—',
    render: () => textCell(null),
  },
  {
    id: 'originator',
    label: 'Originator',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => originatorOf(loan),
    display: (loan) => originatorOf(loan) || '—',
    render: (loan) => textCell(originatorOf(loan) || null),
  },
  {
    id: 'analyst',
    label: 'Analyst',
    group: 'Workflow',
    kind: 'enum',
    value: (loan) => analystOf(loan),
    display: (loan) => analystOf(loan) || '—',
    render: (loan) => textCell(analystOf(loan) || null),
  },
  {
    id: 'closer',
    label: 'Closer',
    group: 'Workflow',
    kind: 'text',
    value: () => '',
    display: () => '—',
    render: () => textCell(null),
  },
  // The quote clock and the closing clock (Chris Young, 26 Oct 2026; CY-08/09/10).
  // Served on every row by `deal_clocks`; `view_grid.py` prints the same cells.
  {
    id: 'quoteLetterDate',
    label: 'Quote letter sent',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loan.dealClocks?.quoteLetterDate ?? '',
    display: (loan) => loan.dealClocks?.quoteLetterDate || '—',
    render: (loan) => textCell(loan.dealClocks?.quoteLetterDate || null),
  },
  {
    // Days a quote has sat at Post-screen Quote, from its letter. Amber once it
    // passes the stale-quote window: the owner's end-of-day digest names it too.
    id: 'quoteAge',
    align: 'right',
    label: 'Days quote out',
    group: 'Workflow',
    kind: 'number',
    unit: 'days',
    value: (loan) => loan.dealClocks?.quoteAgeDays ?? '',
    num: (loan) => loan.dealClocks?.quoteAgeDays ?? null,
    display: (loan) => quoteAgeText(loan),
    render: (loan) => {
      const clocks = loan.dealClocks
      const age = clocks?.quoteAgeDays ?? null
      const title =
        age == null
          ? undefined
          : clocks?.quoteAgedFrom === 'stage_move'
            ? 'No quote letter date recorded — counted from the move to Post-screen Quote'
            : `Quote letter sent ${clocks?.quoteLetterDate}`
      return figureCell(
        quoteAgeText(loan),
        age != null,
        clocks?.quoteStale ? 'text-amber-300' : 'text-stone-300',
        clocks?.quoteStale
          ? `${title}. Out ${clocks.staleQuoteDays} days or more: refresh the price or update the status`
          : title,
      )
    },
  },
  {
    id: 'depositWireDate',
    label: 'Deposit wire received',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loan.dealClocks?.depositWireDate ?? '',
    display: (loan) => loan.dealClocks?.depositWireDate || '—',
    render: (loan) => textCell(loan.dealClocks?.depositWireDate || null),
  },
  {
    // The deposit wire plus the closing window, while the deal is between the
    // wire and funding.
    id: 'closeByDate',
    label: 'Close-by date',
    group: 'Workflow',
    kind: 'date',
    value: (loan) => loan.dealClocks?.closeByDate ?? '',
    display: (loan) => loan.dealClocks?.closeByDate || '—',
    render: (loan) =>
      figureCell(
        loan.dealClocks?.closeByDate || '—',
        Boolean(loan.dealClocks?.closeByDate),
        'text-stone-300',
        loan.dealClocks?.closeByDate
          ? `Deposit wire ${loan.dealClocks.depositWireDate} + ${loan.dealClocks.closingWindowDays} days`
          : undefined,
      ),
  },
  {
    // Counts down to the close-by date; negative once it has passed (red).
    id: 'daysToCloseBy',
    align: 'right',
    label: 'Days to close-by',
    group: 'Workflow',
    kind: 'number',
    unit: 'days',
    value: (loan) => loan.dealClocks?.daysToCloseBy ?? '',
    num: (loan) => loan.dealClocks?.daysToCloseBy ?? null,
    display: (loan) => daysToCloseByText(loan),
    render: (loan) => {
      const left = loan.dealClocks?.daysToCloseBy ?? null
      return figureCell(
        daysToCloseByText(loan),
        left != null,
        left != null && left < 0 ? 'text-rose-300' : 'text-stone-300',
        left != null && left < 0 ? 'Past the close-by date' : undefined,
      )
    },
  },
]

export const COLUMN_DEFS: ColumnDef[] = RECORD_COLUMN_DEFS.map(withBookCell)

function quoteAgeText(loan: Loan): string {
  const age = loan.dealClocks?.quoteAgeDays
  return age == null ? '—' : `${age}d`
}

function daysToCloseByText(loan: Loan): string {
  const left = loan.dealClocks?.daysToCloseBy
  return left == null ? '—' : `${left}d`
}

export const COLUMN_MAP = new Map(COLUMN_DEFS.map((column) => [column.id, column]))

export function columnIds(): ColumnId[] {
  return COLUMN_DEFS.map((column) => column.id)
}
